import {
  Form,
  Input,
  Dropdown,
  FieldRow,
  Tooltip,
  Checkbox,
  Info,
  CurrencyInput,
  MaskedInput,
  Modal,
  ErrorMessage,
  Loading,
  Delete,
  InputUploadFile,
  Grid,
} from '~components';
import { Values, Option } from './types';
import statesOptions from '~constants/federationStates';
import * as Styled from './styled';
import { Props } from './types';
import { Header } from './sections';
import { FormikHelpers } from 'formik';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import { Federation, Address } from '~services';
import { formatCitiesOptions } from '~utils/formatters';
import { onlyNumbers, onlyLetters } from '~constants/regularExpressions';
import { validationRepresentative } from './validation';
import { FuelTypography } from '@frete.com/fuel-react';

function FormSection({
  status,
  name,
  type,
  values,
  removeForm,
  isIndividualCompany,
  onSubmit,
  onChange,
  setIsToastVisibleForm,
  setIsToastVisibleForRepresentativeSavedInMemory,
  onValidationChange,
  ...props
}: Props) {
  const [isFormValid, setIsFormValid] = useState(true);
  const [citiesOptions, setCitiesOptions] = useState([]);
  const [isCardExpanded, setCardExpanded] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [zipCodeError, setZipCodeError] = useState('');
  const [isAllAddressFieldsDisabled, setAllAddressFieldsDisabled] =
    useState(true);

  const { runAsync: fetchAddressByZipCode, loading: loadingAddressByZipCode } =
    useRequest(Address.getAddressByZipCode, {
      onError: () => setZipCodeError('CEP não encontrado.'),
      manual: true,
    });

  const { run: fetchCitiesByStateId, loading: loadingCities } = useRequest(
    Federation.getCitiesByStateId,
    {
      onSuccess: (response) => {
        const formattedCities = formatCitiesOptions(response?.data);
        setCitiesOptions(formattedCities);
      },
      manual: true,
    }
  );

  const inputFileRef = useRef<HTMLInputElement>();
  const isFormPendent = status === 'pendente';
  const isPrimaryRepresentant = type === 'primary';
  const hasAbleToToggleFields = !isPrimaryRepresentant;
  const validateDataShowAllField = isFormPendent ? false : !!values?.cpf;

  const initialValues = {
    allFields: isPrimaryRepresentant || validateDataShowAllField,
    secondaryRepresentative: !isPrimaryRepresentant,
    politicallyExposedPerson: false,
    name: values?.name || '',
    email: values?.email || '',
    cellphone: values?.cellphone || '',
    cpf: values?.cpf || '',
    birthdayDate: values?.birthdayDate || '',
    motherName: values?.motherName || '',
    socialContract: values?.socialContract,
    zipCode: values?.zipCode || '',
    state: values?.state || '',
    city: values?.city || '',
    neighborhood: values?.neighborhood || '',
    street: values?.street || '',
    number: values?.number || '',
    complement: values?.complement || '',
    declaredIncome: values?.declaredIncome || '',
  };

  const fileMaxSizeKB = process.env.REACT_APP_MAX_FILE_SIZE || 4507904;

  function getFileMaxSizeMB() {
    return (fileMaxSizeKB / 1024 / 1024).toFixed(1);
  }

  const handleChangeZipCode = async (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue']
  ) => {
    if (event.target.value.length === 9 && !loadingAddressByZipCode) {
      const zipCodeResponse = await fetchAddressByZipCode(
        event.target.value.replace(onlyNumbers, '')
      );

      setAllAddressFieldsDisabled(false);
      fetchCitiesByStateId(zipCodeResponse.data.state);

      setFieldValue('city', zipCodeResponse.data.city);
      setFieldValue('neighborhood', zipCodeResponse.data.neighborhood);
      setFieldValue('state', zipCodeResponse.data.state);
      setFieldValue('street', zipCodeResponse.data.street);
    } else {
      setFieldValue('city', '');
      setFieldValue('neighborhood', '');
      setFieldValue('state', '');
      setFieldValue('street', '');
      setZipCodeError('');
      setAllAddressFieldsDisabled(true);
    }
    onChange(event);
  };

  const handleChangeOnlyLetters = async (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue'],
    fieldName: string
  ) => {
    const newValue = event.target.value.replace(onlyLetters, '');
    setFieldValue(fieldName, newValue);
    onChange(event);
  };

  const handleChangeCellphoneMask = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue']
  ) => {
    const newValue = event.target.value
      .replace(/\D/g, '')
      .slice(0, 11)
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d)(\d{4})$/, '$1-$2');
    setFieldValue('cellphone', newValue);
  };

  const handleChangeNumber = (
    event: ChangeEvent<HTMLInputElement>,
    setFieldValue: FormikHelpers<Values>['setFieldValue']
  ) => {
    const newValue = event.target.value.replace(/D/, '').slice(0, 6);
    setFieldValue('number', newValue);
  };

  const handleClickUploadButton = () => inputFileRef.current.click();
  const handleSelectState = (state: Option) => fetchCitiesByStateId(state.id);
  const handleClickClose = () => setModalOpen(false);
  const handleClickDelete = () => setModalOpen(true);
  const handleChevronClick = () => setCardExpanded((value) => !value);
  const handleClickCancelDelete = handleClickClose;

  const handleSubmit = async (values: Values) => {
    const declaredIncomeNumber = Number(
      values.declaredIncome.replace(',', '.')
    );

    values.declaredIncome = String(declaredIncomeNumber);

    const isOnSucess = await onSubmit(values);
    isOnSucess && setIsToastVisibleForRepresentativeSavedInMemory(true);
    setCardExpanded(!isOnSucess);
  };

  const modalPrimaryButton = {
    text: 'Excluir',
    onClick: removeForm,
  };

  const modalSecondaryButton = {
    text: 'Cancelar',
    onClick: handleClickCancelDelete,
  };

  function checkErrorsInForm(isValid: boolean) {
    setIsToastVisibleForm(!isValid);
  }

  useEffect(() => {
    onValidationChange(isFormValid);
  }, [isFormValid]);

  return (
    <>
      <Styled.OutlinedCard>
        <Styled.ChevronDown
          onClick={handleChevronClick}
          $isFormPendent={isFormPendent}
          $isCardExpanded={isCardExpanded}
        />
        {!isPrimaryRepresentant && (
          <Styled.WrapperRelative data-testid="card-options">
            <Styled.Dots />
            <Styled.Tooltip onClick={handleClickDelete} data-testid="tooltip">
              <Delete />
              Excluir representante
            </Styled.Tooltip>
          </Styled.WrapperRelative>
        )}
        <Header
          name={values?.name}
          isFormPendent={isFormPendent}
          isCardExpanded={isCardExpanded}
          isPrimaryRepresentant={isPrimaryRepresentant}
        />
        <Form<Values>
          show={isFormPendent || isCardExpanded}
          height="auto"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationRepresentative}
          aria-labelledby="form-register-representant-title"
          enableReinitialize
        >
          {({ values, errors, isValid, touched, ...helpers }) => {
            if (isFormValid !== isValid) {
              setIsFormValid(isValid);
            }

            return (
              <>
                {hasAbleToToggleFields && (
                  <FieldRow>
                    <Checkbox
                      name="allFields"
                      label={
                        <>
                          <Styled.CheckboxLabel>
                            Preencher todos os dados pessoais do representante.
                          </Styled.CheckboxLabel>
                          <Tooltip
                            width="300px"
                            text="Preencha todos os dados pessoais do representante ou indique o número de celular e e-mail para que ele preencha posteriormente."
                            placement="right"
                          >
                            <Info />
                          </Tooltip>
                        </>
                      }
                    />
                  </FieldRow>
                )}
                <FieldRow>
                  <Input
                    name="name"
                    placeholder="Digite seu nome"
                    label="Nome completo *"
                    width="44%"
                    type="text"
                    maxLength={255}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleChangeOnlyLetters(
                        event,
                        helpers.setFieldValue,
                        'name'
                      )
                    }
                  />
                  <Input
                    name="email"
                    placeholder="Digite seu e-mail"
                    label="E-mail *"
                    width="30%"
                    maxLength={50}
                    onChange={onChange}
                  />
                  <Input
                    name="cellphone"
                    placeholder="(00)00000-0000"
                    label="Celular *"
                    width="26%"
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      handleChangeCellphoneMask(event, helpers.setFieldValue)
                    }
                  />
                </FieldRow>

                {values.allFields && (
                  <>
                    <FieldRow>
                      <MaskedInput
                        mask="999.999.999-99"
                        name="cpf"
                        placeholder="Digite seu CPF"
                        label="CPF *"
                        width="30%"
                        onChange={onChange}
                      />
                      <MaskedInput
                        mask="99/99/9999"
                        name="birthdayDate"
                        placeholder="00/00/0000"
                        label="Data de nascimento *"
                        width="25%"
                        onChange={onChange}
                      />
                      <Input
                        name="motherName"
                        placeholder="Nome completo da mãe"
                        label="Nome completo da mãe *"
                        width="50%"
                        type="text"
                        maxLength={255}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChangeOnlyLetters(
                            event,
                            helpers.setFieldValue,
                            'motherName'
                          )
                        }
                      />
                    </FieldRow>
                    <FieldRow>
                      <MaskedInput
                        mask="99999-999"
                        name="zipCode"
                        type="tel"
                        placeholder="Digite seu CEP"
                        label="CEP *"
                        trailingIcon={loadingAddressByZipCode && <Loading />}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChangeZipCode(event, helpers.setFieldValue)
                        }
                        externalError={zipCodeError}
                        width="20%"
                      />
                      <Input
                        name="street"
                        placeholder="Logradouro"
                        label="Logradouro *"
                        width="30%"
                        maxLength={40}
                        disabled={isAllAddressFieldsDisabled}
                        onChange={onChange}
                      />
                      <Input
                        name="number"
                        placeholder="Número"
                        type="number"
                        label="Número *"
                        width="25%"
                        disabled={isAllAddressFieldsDisabled}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          handleChangeNumber(event, helpers.setFieldValue)
                        }
                      />
                      <Input
                        name="neighborhood"
                        placeholder="Nome do bairro"
                        label="Bairro *"
                        width="33%"
                        maxLength={40}
                        disabled={isAllAddressFieldsDisabled}
                        onChange={onChange}
                      />
                    </FieldRow>
                    <FieldRow>
                      <Dropdown
                        name="state"
                        label="Estado *"
                        width="33%"
                        placeholder="Selecione o estado"
                        onSelect={handleSelectState}
                        options={statesOptions}
                        disabled={isAllAddressFieldsDisabled}
                      />
                      <Dropdown
                        name="city"
                        label="Cidade *"
                        placeholder="Selecione a cidade"
                        width="33%"
                        trailingIcon={
                          loadingCities && <Loading withBackground />
                        }
                        options={citiesOptions}
                        disabled={isAllAddressFieldsDisabled}
                      />
                      <Input
                        name="complement"
                        placeholder="Apartamento, sala, condomínio etc."
                        label="Complemento"
                        width="42%"
                        maxLength={30}
                        disabled={isAllAddressFieldsDisabled}
                        onChange={onChange}
                      />
                    </FieldRow>

                    <FieldRow>
                      <CurrencyInput
                        name="declaredIncome"
                        placeholder="Renda declarada mensal"
                        label="Renda declarada mensal *"
                        width="24%"
                        maxLength={15}
                        onChange={onChange}
                      />
                      <Styled.Checkbox
                        name="politicallyExposedPerson"
                        label={
                          <Styled.CheckboxLabel>
                            Pessoa Exposta Politicamente (PEP)?
                          </Styled.CheckboxLabel>
                        }
                        onChange={onChange}
                      />
                    </FieldRow>
                    {isPrimaryRepresentant && (
                      <>
                        <Grid container direction="column" spacing={6}>
                          <Grid>
                            <FuelTypography
                              size="body-md"
                              color="neutral-default"
                            >
                              {isIndividualCompany
                                ? 'Precisamos que você envie o certificado MEI:'
                                : 'Precisamos que você envie o contrato social da empresa com a última atualização na junta comercial:'}
                            </FuelTypography>
                          </Grid>
                          <Grid container direction="column">
                            <Grid>
                              <FuelTypography
                                size="caption"
                                variant="bold"
                                color="neutral-default"
                              >
                                Formato PDF até {getFileMaxSizeMB()}MB.
                              </FuelTypography>
                            </Grid>
                            <Grid>
                              <Styled.ButtonWithClip
                                onClick={handleClickUploadButton}
                              >
                                {isIndividualCompany
                                  ? 'Anexar Certificado MEI*'
                                  : 'Anexar contrato social *'}
                              </Styled.ButtonWithClip>
                              <InputUploadFile
                                data-testid="social_contract"
                                name="socialContract"
                                accept="application/pdf"
                                hidden
                                inputFileRef={inputFileRef}
                              />
                            </Grid>
                            {errors.socialContract && touched.socialContract && (
                              <Grid>
                                <ErrorMessage>
                                  {errors.socialContract}
                                </ErrorMessage>
                              </Grid>
                            )}
                            {!errors.socialContract && !!values.socialContract && (
                              <Grid>
                                <FuelTypography
                                  size="caption"
                                  color="success-default"
                                  variant="bold"
                                >
                                  Documento anexado com sucesso.
                                </FuelTypography>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </>
                )}

                <Styled.Footer $hasSpacingTop={values.allFields}>
                  <Styled.ButtonWithUser
                    onClick={() => checkErrorsInForm(isValid)}
                    type="submit"
                  >
                    Salvar representante
                  </Styled.ButtonWithUser>
                </Styled.Footer>
              </>
            );
          }}
        </Form>
      </Styled.OutlinedCard>
      <Modal
        open={isModalOpen}
        onClose={handleClickClose}
        title="Excluir representante"
        message="Você tem certeza que deseja excluir este representante?"
        highlightedMessage={values?.name}
        buttons={[modalSecondaryButton, modalPrimaryButton]}
      />
    </>
  );
}

export default FormSection;
